import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ActivityIndicator } from 'react-native';
import styled from 'styled-components/native';

import { FormSectionMeta } from '@people-science/forms-core';
import {
  StateEventConfig,
  StateMachineNextEvent,
  StateNodeConfig,
  SubscriptionType
} from '@people-science/state-machine';

import Button from '../components/Button';
import Link from '../components/Link';
import Spacer from '../components/Spacer';
import { H4, P } from '../components/Typography';
import Colors from '../constants/Colors';
import { useDynamicFormContext } from './DynamicFormContext';
import FormElement from './FormElement';
import AlertDialog from '../components/AlertDialog';
import { StackActions, useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import accessibilityTags from '../../accessibilityTags';
import ConfirmDialog from '../components/ConfirmDialog';
import { FetchError } from '../api/api';
import { ErrorCode } from '../common/error-codes';

const StyledView = styled.View`
  align-self: center;
  width: 100%;
  flex: 1 1 auto;
  max-width: 678px;
  padding: 24px 20px 24px 20px;
`;

const StyledBackButton = styled(Button)`
  background-color: transparent;
  margin-bottom: 24px;
  align-self: flex-start;
`;

const StyledButtonView = styled.View`
  margin-top: 20px;
  margin-bottom: 50px;
  width: 100%;
  flex-direction: row;
  align-items: center;
  /* justify-content: flex-end; */
`;

const StyledRowView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledDescription = styled(P)<{ size?: 'normal' | 'large' }>`
  font-size: ${p => (p.size === 'large' ? 22 : 20)}px;
  line-height: ${p => (p.size === 'large' ? 32 : 24)}px;
`;

const StyledEventsView = styled.View`
  width: 100%;
  flex: 1 1 auto;
  margin-top: 12px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 40px;
  /* border-width: 1.5px;
  border-radius: 20px; */
`;

const StyledLink = styled(Link)`
  margin-top: 24px;
  text-align: center;
`;

const StyledErrorText = styled(P)`
  color: ${Colors.error};
`;

type ChloeFormSectionMeta<FormValuesType, FormElementConfigType> =
  FormSectionMeta<FormValuesType, FormElementConfigType> & {
    descriptionSize?: 'normal' | 'large';
  };

export interface FormSectionProps<FormValuesType, FormElementConfigType> {
  stateNode: StateNodeConfig<
    ChloeFormSectionMeta<FormValuesType, FormElementConfigType>,
    any
  >;
  edit?: boolean;
  backTitle?: string;
  appendSectionControls?: JSX.Element;
  disabled?: boolean;
  hideTransitions?: boolean;
  onTransition?: (event: StateEventConfig) => void;
  onBack?: () => void;
}

export const FormSection = <
  FormValuesType extends object,
  FormElementConfigType extends object
>({
  stateNode,
  edit,
  backTitle = 'Back',
  appendSectionControls,
  disabled,
  hideTransitions,
  onTransition,
  onBack
}: FormSectionProps<FormValuesType, FormElementConfigType>) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const [generalErrorDialog, setGeneralErrorDialog] = useState<{
    message: string;
    title: string;
  }>();
  const [showNotRecruitingError, setShowNotRecruitingError] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{
    error: Boolean;
    event?: StateEventConfig<any>;
  }>({ error: false });
  const {
    formValues,
    isSubmitting,
    setIsSubmitting,
    error,
    stateMachine,
    setFormValues,
    disableEventTransition,
    elementPlugins
  } = useDynamicFormContext<FormValuesType, FormElementConfigType>();
  // Check which next events can transition
  const mountRef = useRef(false);
  const [nextEvents, setNextEvents] = useState<StateMachineNextEvent[]>([]);
  const updateNextEvents = useCallback(() => {
    stateMachine?.nextEvents().then(nextEvents => {
      // prevent unmount component state change situation
      if (mountRef.current) {
        setNextEvents(nextEvents);
      }
    });
  }, [stateMachine]);
  useEffect(() => {
    if (!stateMachine) return;
    mountRef.current = true;
    // don't subscribe if in edit mode
    if (edit) return;
    updateNextEvents(); // make sure to check next events on mount
    const onEnd = () => {
      updateNextEvents();
    };
    stateMachine.subscribe(SubscriptionType.TransitionEnd, onEnd);
    return () => {
      mountRef.current = false;
      stateMachine.unsubscribe(SubscriptionType.TransitionEnd, onEnd);
    };
  }, [edit, stateMachine, updateNextEvents]);

  // Refresh next events on form value changes
  useEffect(() => {
    if (formValues) updateNextEvents();
  }, [formValues, updateNextEvents]);

  // if error disable submitting
  useEffect(() => {
    if (error) {
      setIsSubmitting(false);
      if ((error as FetchError).code === ErrorCode.FORM_TIME_TRAVEL_ERROR) {
        setGeneralErrorDialog({
          message: `Cannot fill out the form at this time. Please wait until the event's scheduled start date to be able to fill out this form. Thank you!`,
          title: 'Form cannot be completed at this time.'
        });
      } else if (
        (error as FetchError).code === ErrorCode.STUDY_NOT_RECRUITING
      ) {
        setShowNotRecruitingError(true);
      } else if ((error as FetchError).statusCode !== 500) {
        setGeneralErrorDialog({
          message: error.message,
          title: 'Error'
        });
      } else {
        setErrorDialog(prev => ({ ...prev, error: true }));
      }
    }
  }, [error, setIsSubmitting]);

  const { meta } = stateNode;

  // Trigger formValues change for stateNodes that have an action to force
  // triggering action if no form data changes for current state (for data
  // calculation purposes)
  useEffect(() => {
    if (
      mountRef.current &&
      !stateMachine?.isTransitioning &&
      stateNode.onEntry
    ) {
      setFormValues(formValues => ({ ...formValues }));
    }
  }, [setFormValues, stateMachine?.isTransitioning, stateNode]);

  // check if all required fields are completed
  const hasCompletedSection = useMemo(() => {
    for (const element of meta?.elements || []) {
      const value = (formValues as any)[element.dataSource || element.key];
      const plugin = elementPlugins.find(ep => ep.type === element.type);
      if (plugin?.validate) {
        const valid = plugin.validate(element, formValues);
        if (!valid) return false;
      }
      const isTextField =
        element.type === 'TextField' || element.type === 'PhoneNumber';

      if (
        element.required &&
        (value === null ||
          typeof value === 'undefined' ||
          (Array.isArray(value) && value.length === 0) ||
          (isTextField && !value))
      ) {
        return false;
      }
    }
    return true;
  }, [formValues, meta?.elements, elementPlugins]);

  const hideSectionHeader =
    meta?.elements?.find(element =>
      ['AssignStudyTemplate', 'ParticipantStatusChange'].includes(element.type)
    ) !== undefined;

  const displayableEvents = stateNode.events?.filter(e => !e.isTransient) || [];

  const navigateAway = (to: 'todo' | 'join') => {
    navigation.dispatch(
      StackActions.replace('DrawerNavigator', {
        screen: 'BottomTabNav',
        params: {
          screen: to === 'todo' ? 'TodoNav' : 'DiscoverNav'
        }
      })
    );
  };

  return (
    <StyledView>
      {/* {Boolean(onBack) && meta?.showBackButton && page > 0 && (
        <StyledBackButton
          inverse
          startIcon={
            <Feather name='arrow-left' size={24} color={Colors.primaryPurple} />
          }
          onPress={() => onBack?.()}
        >
          {backTitle}
        </StyledBackButton>
      )} */}
      {!meta?.hideTitle && !!meta?.title && !hideSectionHeader && (
        <H4 padding={1} color='dark'>
          {meta.title}
        </H4>
      )}
      {!!meta?.description && !hideSectionHeader && (
        <StyledDescription color='dark' padding={4} size={meta.descriptionSize}>
          {meta.description}
        </StyledDescription>
      )}
      {meta?.elements?.map((element, index) => (
        <FormElement
          data-index={index}
          key={index}
          edit={edit}
          stateNode={stateNode}
          element={element}
          disabled={disabled}
        />
      ))}
      <StyledEventsView>
        {displayableEvents.length > 0
          ? displayableEvents.map((event, index) => {
              const isDisabled =
                isSubmitting ||
                edit ||
                disableEventTransition ||
                !hasCompletedSection ||
                !nextEvents.find(e => e.name === event.name)?.canTransition;
              return event.data?.isTextLink ? (
                <StyledRowView>
                  <StyledLink
                    inverse
                    key={`${event.name}_${index}`}
                    disabled={isDisabled}
                    onPress={() => {
                      if (isSubmitting || isDisabled) return;
                      setIsSubmitting(true);
                      setErrorDialog({ error: false, event });
                      onTransition?.(event);
                    }}
                  >
                    {event.name || 'New Event'}
                  </StyledLink>
                  {isSubmitting && (
                    <ActivityIndicator
                      size='small'
                      style={{ top: 12, marginLeft: 4 }}
                      color={Colors.buttonDisabled}
                    />
                  )}
                </StyledRowView>
              ) : (
                <StyledButton
                  accessibilityLabel={
                    accessibilityTags.progressionButtonID +
                    ` ${event.name?.toUpperCase()}`
                  }
                  testID={
                    accessibilityTags.progressionButtonID +
                    ` ${event.name?.toUpperCase()}`
                  }
                  key={`${event.name}_${index}`}
                  // TOOD: canTransition is a promise; so need to do async
                  disabled={isDisabled}
                  endIcon={
                    isSubmitting && (
                      <ActivityIndicator size='small' color='#fff' />
                    )
                  }
                  onPress={() => {
                    setIsSubmitting(true);
                    setErrorDialog({ error: false, event });
                    onTransition?.(event);
                  }}
                >
                  {event.name || 'New Event'}
                </StyledButton>
              );
            })
          : isSubmitting && (
              <ActivityIndicator size='small' color={Colors.primaryPurple} />
            )}
        <Spacer size={2} />
        {errorDialog.error && errorDialog.event && (
          <ConfirmDialog
            confirmLabel='Try Again'
            onCancelButtonClicked={() => setErrorDialog({ error: false })}
            onConfirmButtonClicked={() => {
              if (errorDialog.event) {
                onTransition?.(errorDialog.event);
              }
              setErrorDialog(prev => ({ ...prev, error: false }));
            }}
            title={'Error'}
            message={'Unable to connect to server. Please try again later.'}
          />
        )}
        {showNotRecruitingError && (
          <ConfirmDialog
            confirmLabel='Checkout Other Studies'
            cancelLabel='Exit'
            onCancelButtonClicked={() => {
              setShowNotRecruitingError(false);
              navigateAway('todo');
            }}
            onConfirmButtonClicked={() => {
              setShowNotRecruitingError(false);
              navigateAway('join');
            }}
            title={'Cannot join study'}
            message={
              error?.message || 'Study not actively recruiting participants.'
            }
          />
        )}
        {generalErrorDialog && (
          <AlertDialog
            title={generalErrorDialog.title}
            message={generalErrorDialog.message}
            onCloseClicked={() => {
              setGeneralErrorDialog(undefined);
              navigateAway('todo');
            }}
          />
        )}
        <Spacer size={2} />
        {/* <PageControl
          total={stateMachine?.stateNode.states.length || 0}
          page={page + 1}
          onBackwardButtonPress={() => onBack?.()}
          onForwardButtonPress={() => {
            // NOTE: forward button probably won't work given that the form
            //transitions could have multiple events.
        /> */}
      </StyledEventsView>
    </StyledView>
  );
};

export default FormSection;
